import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Zap, LogOut, Users, FileText, Cpu, Mail, Trash2, CheckCheck, Plus, Edit2, Eye, EyeOff, X, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const getAuthHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('biovex_admin_token')}` });

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};

// ── Article Modal ─────────────────────────────────────────────────────────────
function ArticleModal({ article, onClose, onSaved }) {
  const isEdit = Boolean(article?.id);
  const [form, setForm] = useState({
    title: article?.title || '',
    excerpt: article?.excerpt || '',
    content: article?.content || '',
    category: article?.category || 'Research',
    author: article?.author || 'Biovex Research Team',
    image_url: article?.image_url || '',
    published: article?.published ?? true,
    tags: (article?.tags || []).join(', '),
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.title || !form.excerpt || !form.content) {
      toast.error('Title, excerpt and content are required.');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean) };
      delete payload.tags_raw;
      if (isEdit) {
        await axios.put(`${API}/api/articles/${article.id}`, payload, { headers: getAuthHeader() });
        toast.success('Article updated.');
      } else {
        await axios.post(`${API}/api/articles`, payload, { headers: getAuthHeader() });
        toast.success('Article created.');
      }
      onSaved();
      onClose();
    } catch {
      toast.error('Failed to save article.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="glass-card w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="article-modal">
        <div className="p-6 border-b border-bvx-border flex items-center justify-between">
          <h2 className="font-outfit font-bold text-white text-lg">{isEdit ? 'Edit Article' : 'New Article'}</h2>
          <button onClick={onClose} className="text-white/40 hover:text-white transition-colors duration-200"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4">
          {[
            { label: 'Title', key: 'title', placeholder: 'Article title' },
            { label: 'Excerpt', key: 'excerpt', placeholder: 'Brief summary', multiline: true, rows: 2 },
            { label: 'Content', key: 'content', placeholder: 'Full article content...', multiline: true, rows: 8 },
            { label: 'Category', key: 'category', placeholder: 'Research' },
            { label: 'Author', key: 'author', placeholder: 'Author name' },
            { label: 'Image URL', key: 'image_url', placeholder: 'https://...' },
            { label: 'Tags (comma-separated)', key: 'tags', placeholder: 'Tag1, Tag2' },
          ].map(({ label, key, placeholder, multiline, rows }) => (
            <div key={key}>
              <label className="block font-rajdhani font-semibold uppercase tracking-widest text-white/40 text-xs mb-1.5">{label}</label>
              {multiline ? (
                <textarea value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} placeholder={placeholder} rows={rows}
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 focus:border-bvx-cyan/50 focus:outline-none text-white text-sm font-inter-tight placeholder:text-white/25 transition-colors duration-200 resize-none" />
              ) : (
                <input value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} placeholder={placeholder}
                  className="w-full h-10 px-4 rounded-lg bg-white/5 border border-white/10 focus:border-bvx-cyan/50 focus:outline-none text-white text-sm font-inter-tight placeholder:text-white/25 transition-colors duration-200" />
              )}
            </div>
          ))}
          <div className="flex items-center gap-3">
            <input type="checkbox" id="published" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} className="accent-bvx-cyan w-4 h-4" />
            <label htmlFor="published" className="text-white/60 text-sm font-inter-tight">Published (visible on site)</label>
          </div>
        </div>
        <div className="p-6 border-t border-bvx-border flex gap-3 justify-end">
          <button onClick={onClose} className="btn-outline text-sm px-5 py-2" data-testid="article-modal-cancel">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="btn-primary flex items-center gap-2 text-sm px-5 py-2 disabled:opacity-50" data-testid="article-modal-save">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {isEdit ? 'Update' : 'Create'}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

// ── Project Modal ─────────────────────────────────────────────────────────────
function ProjectModal({ project, onClose, onSaved }) {
  const isEdit = Boolean(project?.id);
  const [form, setForm] = useState({
    name: project?.name || '',
    tagline: project?.tagline || '',
    description: project?.description || '',
    status: project?.status || 'research',
    image_url: project?.image_url || '',
    tags: (project?.tags || []).join(', '),
    order: project?.order ?? 0,
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.name || !form.tagline || !form.description) {
      toast.error('Name, tagline and description are required.');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean), order: Number(form.order) };
      if (isEdit) {
        await axios.put(`${API}/api/projects/${project.id}`, payload, { headers: getAuthHeader() });
        toast.success('Project updated.');
      } else {
        await axios.post(`${API}/api/projects`, payload, { headers: getAuthHeader() });
        toast.success('Project created.');
      }
      onSaved();
      onClose();
    } catch {
      toast.error('Failed to save project.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="glass-card w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="project-modal">
        <div className="p-6 border-b border-bvx-border flex items-center justify-between">
          <h2 className="font-outfit font-bold text-white text-lg">{isEdit ? 'Edit Project' : 'New Project'}</h2>
          <button onClick={onClose} className="text-white/40 hover:text-white transition-colors duration-200"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4">
          {[
            { label: 'Name', key: 'name', placeholder: 'Project name' },
            { label: 'Tagline', key: 'tagline', placeholder: 'Short tagline' },
            { label: 'Description', key: 'description', placeholder: 'Full description...', multiline: true, rows: 6 },
            { label: 'Image URL', key: 'image_url', placeholder: 'https://...' },
            { label: 'Tags (comma-separated)', key: 'tags', placeholder: 'Tag1, Tag2' },
          ].map(({ label, key, placeholder, multiline, rows }) => (
            <div key={key}>
              <label className="block font-rajdhani font-semibold uppercase tracking-widest text-white/40 text-xs mb-1.5">{label}</label>
              {multiline ? (
                <textarea value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} placeholder={placeholder} rows={rows}
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 focus:border-bvx-cyan/50 focus:outline-none text-white text-sm font-inter-tight placeholder:text-white/25 transition-colors duration-200 resize-none" />
              ) : (
                <input value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} placeholder={placeholder}
                  className="w-full h-10 px-4 rounded-lg bg-white/5 border border-white/10 focus:border-bvx-cyan/50 focus:outline-none text-white text-sm font-inter-tight placeholder:text-white/25 transition-colors duration-200" />
              )}
            </div>
          ))}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block font-rajdhani font-semibold uppercase tracking-widest text-white/40 text-xs mb-1.5">Status</label>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="w-full h-10 px-4 rounded-lg bg-white/5 border border-white/10 focus:border-bvx-cyan/50 focus:outline-none text-white text-sm font-inter-tight transition-colors duration-200">
                {['research', 'prototype', 'development', 'deployed'].map((s) => <option key={s} value={s} className="bg-bvx-card">{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block font-rajdhani font-semibold uppercase tracking-widest text-white/40 text-xs mb-1.5">Display Order</label>
              <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} min={0}
                className="w-full h-10 px-4 rounded-lg bg-white/5 border border-white/10 focus:border-bvx-cyan/50 focus:outline-none text-white text-sm font-inter-tight transition-colors duration-200" />
            </div>
          </div>
        </div>
        <div className="p-6 border-t border-bvx-border flex gap-3 justify-end">
          <button onClick={onClose} className="btn-outline text-sm px-5 py-2" data-testid="project-modal-cancel">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="btn-primary flex items-center gap-2 text-sm px-5 py-2 disabled:opacity-50" data-testid="project-modal-save">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {isEdit ? 'Update' : 'Create'}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

// ── Main Dashboard ────────────────────────────────────────────────────────────
export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('contacts');
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [articles, setArticles] = useState([]);
  const [projects, setProjects] = useState([]);
  const [pitchRequests, setPitchRequests] = useState([]);
  const [articleModal, setArticleModal] = useState(null);
  const [projectModal, setProjectModal] = useState(null);

  const token = localStorage.getItem('biovex_admin_token');

  useEffect(() => {
    if (!token) { navigate('/admin'); return; }
    // Verify token
    axios.get(`${API}/api/admin/me`, { headers: getAuthHeader() })
      .catch(() => { localStorage.removeItem('biovex_admin_token'); navigate('/admin'); });
  }, [token, navigate]);

  const fetchAll = useCallback(async () => {
    if (!token) return;
    const h = { headers: getAuthHeader() };
    const [s, c, a, p, pr] = await Promise.all([
      axios.get(`${API}/api/stats`, h).then((r) => r.data).catch(() => null),
      axios.get(`${API}/api/contacts`, h).then((r) => r.data).catch(() => []),
      axios.get(`${API}/api/articles?published_only=false`, h).then((r) => r.data).catch(() => []),
      axios.get(`${API}/api/projects`).then((r) => r.data).catch(() => []),
      axios.get(`${API}/api/pitch-requests`, h).then((r) => r.data).catch(() => []),
    ]);
    setStats(s);
    setContacts(c);
    setArticles(a);
    setProjects(p);
    setPitchRequests(pr);
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleLogout = () => {
    localStorage.removeItem('biovex_admin_token');
    navigate('/admin');
  };

  const markRead = async (id) => {
    await axios.put(`${API}/api/contacts/${id}/read`, {}, { headers: getAuthHeader() });
    setContacts((prev) => prev.map((c) => c.id === id ? { ...c, status: 'read' } : c));
  };

  const deleteContact = async (id) => {
    await axios.delete(`${API}/api/contacts/${id}`, { headers: getAuthHeader() });
    setContacts((prev) => prev.filter((c) => c.id !== id));
    toast.success('Contact deleted.');
  };

  const deleteArticle = async (id) => {
    await axios.delete(`${API}/api/articles/${id}`, { headers: getAuthHeader() });
    setArticles((prev) => prev.filter((a) => a.id !== id));
    toast.success('Article deleted.');
  };

  const deleteProject = async (id) => {
    await axios.delete(`${API}/api/projects/${id}`, { headers: getAuthHeader() });
    setProjects((prev) => prev.filter((p) => p.id !== id));
    toast.success('Project deleted.');
  };

  const deletePitchRequest = async (id) => {
    await axios.delete(`${API}/api/pitch-requests/${id}`, { headers: getAuthHeader() });
    setPitchRequests((prev) => prev.filter((r) => r.id !== id));
    toast.success('Request deleted.');
  };

  const statCards = [
    { icon: Mail, label: 'Total Contacts', value: stats?.total_contacts ?? '—', color: 'text-bvx-cyan' },
    { icon: Users, label: 'Unread Messages', value: stats?.unread_contacts ?? '—', color: 'text-yellow-400' },
    { icon: FileText, label: 'Total Articles', value: stats?.total_articles ?? '—', color: 'text-bvx-green' },
    { icon: Cpu, label: 'Total Projects', value: stats?.total_projects ?? '—', color: 'text-purple-400' },
    { icon: Users, label: 'Pitch Requests', value: stats?.pitch_requests ?? '—', color: 'text-orange-400' },
  ];

  const tabs = [
    { id: 'contacts', label: 'Contacts', count: contacts.filter((c) => c.status === 'unread').length },
    { id: 'pitch', label: 'Pitch Requests', count: pitchRequests.length },
    { id: 'articles', label: 'Articles', count: articles.length },
    { id: 'projects', label: 'Projects', count: projects.length },
  ];

  return (
    <div className="min-h-screen bg-bvx-bg">
      {/* Header */}
      <header className="border-b border-bvx-border px-6 md:px-12 py-4 flex items-center justify-between glass sticky top-0 z-40">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-bvx-cyan/20 border border-bvx-cyan/40 flex items-center justify-center">
            <Zap size={14} className="text-bvx-cyan" />
          </div>
          <span className="font-outfit font-bold text-white text-base">BIOVEX<span className="text-bvx-cyan">.</span> Admin</span>
        </div>
        <button onClick={handleLogout} className="flex items-center gap-2 text-white/40 hover:text-white text-sm font-inter-tight transition-colors duration-200" data-testid="logout-btn">
          <LogOut size={15} /> Logout
        </button>
      </header>

      <div className="px-6 md:px-12 py-8 max-w-7xl">
        {/* Stats */}
        <motion.div variants={fadeUp} initial="hidden" animate="visible" className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          {statCards.map(({ icon: Icon, label, value, color }, i) => (
            <div key={label} className="glass-card p-5" data-testid={`stat-card-${i}`}>
              <Icon size={18} className={`${color} mb-3`} />
              <p className={`font-outfit font-bold text-3xl ${color}`}>{value}</p>
              <p className="text-white/45 text-xs font-inter-tight mt-1">{label}</p>
            </div>
          ))}
        </motion.div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-bvx-border pb-0">
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
              className={`px-5 py-2.5 text-sm font-inter-tight border-b-2 transition-colors duration-200 flex items-center gap-2 -mb-px ${
                tab === t.id ? 'border-bvx-cyan text-bvx-cyan' : 'border-transparent text-white/40 hover:text-white'
              }`}>
              {t.label}
              {t.count > 0 && <span className={`text-xs px-2 py-0.5 rounded-full ${tab === t.id ? 'bg-bvx-cyan/20 text-bvx-cyan' : 'bg-white/10 text-white/50'}`}>{t.count}</span>}
            </button>
          ))}
        </div>

        {/* Contacts Tab */}
        {tab === 'contacts' && (
          <motion.div variants={fadeUp} initial="hidden" animate="visible" data-testid="contacts-panel">
            {contacts.length === 0 ? (
              <p className="text-white/40 text-sm font-inter-tight py-12 text-center">No contact submissions yet.</p>
            ) : (
              <div className="space-y-3">
                {contacts.map((c) => (
                  <div key={c.id} className={`glass-card p-5 ${c.status === 'unread' ? 'border-bvx-cyan/30' : ''}`} data-testid={`contact-item-${c.id}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-1">
                          <p className="font-outfit font-semibold text-white text-base">{c.name}</p>
                          {c.status === 'unread' && <span className="text-xs bg-bvx-cyan/20 text-bvx-cyan border border-bvx-cyan/30 px-2 py-0.5 rounded-full font-rajdhani uppercase tracking-widest">New</span>}
                        </div>
                        <p className="text-bvx-cyan text-xs font-inter-tight mb-2">{c.email}</p>
                        <p className="text-white/60 text-sm font-inter-tight leading-relaxed">{c.message}</p>
                        <p className="text-white/25 text-xs font-inter-tight mt-2">{new Date(c.created_at).toLocaleString()}</p>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        {c.status === 'unread' && (
                          <button onClick={() => markRead(c.id)} data-testid={`mark-read-${c.id}`}
                            className="p-2 rounded-lg bg-bvx-green/10 border border-bvx-green/20 text-bvx-green hover:bg-bvx-green/20 transition-colors duration-200" title="Mark as read">
                            <CheckCheck size={14} />
                          </button>
                        )}
                        <button onClick={() => deleteContact(c.id)} data-testid={`delete-contact-${c.id}`}
                          className="p-2 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20 transition-colors duration-200" title="Delete">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* Pitch Requests Tab */}
        {tab === 'pitch' && (
          <motion.div variants={fadeUp} initial="hidden" animate="visible" data-testid="pitch-panel">
            {pitchRequests.length === 0 ? (
              <p className="text-white/40 text-sm font-inter-tight py-12 text-center">No pitch deck requests yet.</p>
            ) : (
              <div className="space-y-3">
                {pitchRequests.map((r) => (
                  <div key={r.id} className="glass-card p-5 border-orange-400/20" data-testid={`pitch-item-${r.id}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-1 flex-wrap">
                          <p className="font-outfit font-semibold text-white text-base">{r.name}</p>
                          {r.investment_range && (
                            <span className="text-xs bg-orange-500/15 text-orange-400 border border-orange-500/25 px-2 py-0.5 rounded-full font-rajdhani uppercase tracking-widest">
                              {r.investment_range}
                            </span>
                          )}
                        </div>
                        <p className="text-bvx-cyan text-xs font-inter-tight mb-1">{r.email}</p>
                        {r.organization && <p className="text-white/50 text-xs font-inter-tight mb-1">{r.organization}</p>}
                        {r.message && <p className="text-white/55 text-sm font-inter-tight leading-relaxed">{r.message}</p>}
                        <p className="text-white/25 text-xs font-inter-tight mt-2">{new Date(r.created_at).toLocaleString()}</p>
                      </div>
                      <button onClick={() => deletePitchRequest(r.id)} data-testid={`delete-pitch-${r.id}`}
                        className="p-2 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20 transition-colors duration-200 shrink-0">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* Articles Tab */}
        {tab === 'articles' && (
          <motion.div variants={fadeUp} initial="hidden" animate="visible" data-testid="articles-panel">
            <div className="flex justify-end mb-5">
              <button onClick={() => setArticleModal({})} className="btn-primary flex items-center gap-2 text-sm px-5 py-2" data-testid="new-article-btn">
                <Plus size={14} /> New Article
              </button>
            </div>
            {articles.length === 0 ? (
              <p className="text-white/40 text-sm font-inter-tight py-12 text-center">No articles yet.</p>
            ) : (
              <div className="space-y-3">
                {articles.map((a) => (
                  <div key={a.id} className="glass-card p-5 flex items-start justify-between gap-4" data-testid={`article-item-${a.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-outfit font-semibold text-white text-base">{a.title}</p>
                        {!a.published && <span className="text-xs bg-yellow-500/15 text-yellow-400 border border-yellow-500/25 px-2 py-0.5 rounded-full font-rajdhani uppercase tracking-widest">Draft</span>}
                      </div>
                      <p className="text-white/45 text-xs font-inter-tight mb-1">{a.category} · {a.author}</p>
                      <p className="text-white/50 text-sm font-inter-tight line-clamp-1">{a.excerpt}</p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button onClick={() => setArticleModal(a)} data-testid={`edit-article-${a.id}`}
                        className="p-2 rounded-lg bg-bvx-cyan/10 border border-bvx-cyan/20 text-bvx-cyan hover:bg-bvx-cyan/20 transition-colors duration-200">
                        <Edit2 size={14} />
                      </button>
                      <button onClick={() => deleteArticle(a.id)} data-testid={`delete-article-${a.id}`}
                        className="p-2 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20 transition-colors duration-200">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* Projects Tab */}
        {tab === 'projects' && (
          <motion.div variants={fadeUp} initial="hidden" animate="visible" data-testid="projects-panel">
            <div className="flex justify-end mb-5">
              <button onClick={() => setProjectModal({})} className="btn-primary flex items-center gap-2 text-sm px-5 py-2" data-testid="new-project-btn">
                <Plus size={14} /> New Project
              </button>
            </div>
            {projects.length === 0 ? (
              <p className="text-white/40 text-sm font-inter-tight py-12 text-center">No projects yet.</p>
            ) : (
              <div className="space-y-3">
                {projects.map((p) => (
                  <div key={p.id} className="glass-card p-5 flex items-start justify-between gap-4" data-testid={`project-item-${p.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1">
                        <p className="font-outfit font-semibold text-white text-base">{p.name}</p>
                        <span className={`status-badge status-${p.status}`}>{p.status}</span>
                      </div>
                      <p className="text-bvx-cyan text-xs font-inter-tight mb-1">{p.tagline}</p>
                      <p className="text-white/50 text-sm font-inter-tight line-clamp-1">{p.description}</p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button onClick={() => setProjectModal(p)} data-testid={`edit-project-${p.id}`}
                        className="p-2 rounded-lg bg-bvx-cyan/10 border border-bvx-cyan/20 text-bvx-cyan hover:bg-bvx-cyan/20 transition-colors duration-200">
                        <Edit2 size={14} />
                      </button>
                      <button onClick={() => deleteProject(p.id)} data-testid={`delete-project-${p.id}`}
                        className="p-2 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20 transition-colors duration-200">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </div>

      {/* Modals */}
      {articleModal !== null && (
        <ArticleModal
          article={Object.keys(articleModal).length > 0 ? articleModal : null}
          onClose={() => setArticleModal(null)}
          onSaved={fetchAll}
        />
      )}
      {projectModal !== null && (
        <ProjectModal
          project={Object.keys(projectModal).length > 0 ? projectModal : null}
          onClose={() => setProjectModal(null)}
          onSaved={fetchAll}
        />
      )}
    </div>
  );
}
